import { BehaviorSubject, Observable, Subscription, throwError } from 'rxjs';
import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { TreeFilterData, TreeFilterMappedIds } from '../../_models/contractManagement/common';
import { CommonContractDataService } from './common.service';
import { FarmService } from '../farm.service';
import { environment } from '../../../../environments/environment';

@Injectable()
export class TreeFilterService implements OnDestroy {
  private subscriptions = new Subscription();
  private apiBaseUrl = environment.apiBaseUrl;
  private apiContractsUrl = environment.apiContractsUrl;
  private headers = new HttpHeaders();
  // this observables are related to the filters on the contract management dashboard
  // any time they change they trigger the stats to be updated
  transformerFilter$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  traderFilter$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  stackerFilter$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  farmFilter$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  chainFilter$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  treeFilterChildren$: BehaviorSubject<TreeFilterData[]> = new BehaviorSubject([]);

  constructor(
    private http: HttpClient,
    private farmService: FarmService,
    private commonContractDataService: CommonContractDataService
  ) {
    // Appending the headers needed to autenticate
    // the user role for the contracts microservice
    // Subscribe to selectedFarmGroup$
    this.subscriptions.add(
      this.farmService.selectedFarmGroup$.subscribe((g) => {
        if (g) {
          // Update headers with chainKey and role
          this.headers = this.headers
            .set('chainKey', g.group)
            .set('role', g.rolesInGroup[0]?.authority || '');
        } else {
          //should logout? or redirect
        }
      })
    );
  }

  // Updates chain filter value generating a new array and verifyng that the passed id are not alrrady existing in the previous array
  updateChainFilter(filter: any[]) {
    this.chainFilter$.next([...filter]);
  }

  // Gets the tree filter data
  getTreeFilterChildren(): Observable<TreeFilterMappedIds[]> {
    return this.treeFilterChildren$.asObservable();
  }

  updateTreeFilterChildren(treeFilterChildren: TreeFilterData[]) {
    this.treeFilterChildren$.next(treeFilterChildren);
  }

  getTreeFilterContent(values: TreeFilterData[]): Observable<TreeFilterData[]> {
    return this.http
      .post<TreeFilterData[]>(
        `${this.apiBaseUrl}api/private/v1/multiFarm/supplierRelations?group=${this.headers.get('chainKey')}`,
        values.map((el) => el.companyId),
        { headers: this.headers }
      )
      .pipe(
        map((response) => {
          // Returning the raw content of the tree filters
          return this.filterAndFlattenData(response);
        }),
        catchError((err: any) => {
          return throwError(() => err);
        })
      );
  }

  // Filters out children not in common between all the required companies
  filterAndFlattenData(data: TreeFilterData[]) {
    function findCommonObjects(arrays) {
      if (arrays.length === 0) {
        return [];
      }

      const firstArray = arrays[0];

      // Use the filter method to iterate through the objects in the first array
      // and check if their IDs exist in all other arrays using the every method
      const commonObjects = firstArray.filter((object) => {
        const { companyId } = object;
        return arrays.every((array) => array.some((item) => item.companyId === companyId));
      });

      return commonObjects;
    }

    const transformer = [];
    const trader = [];
    const stacker = [];
    const farm = [];

    data.forEach((el) => {
      if (
        el.commonChildren &&
        el.commonChildren.length &&
        el.roles[0].rolesInGroup.includes('TRANSFORMER')
      ) {
        transformer.push(...el.commonChildren);
      }
      if (
        el.commonChildren &&
        el.commonChildren.length &&
        el.roles[0].rolesInGroup.includes('TRADER')
      ) {
        trader.push(...el.commonChildren);
      }
      if (
        el.commonChildren &&
        el.commonChildren.length &&
        el.roles[0].rolesInGroup.includes('STACKER')
      ) {
        stacker.push(...el.commonChildren);
      }
      if (
        el.commonChildren &&
        el.commonChildren.length &&
        el.roles[0].rolesInGroup.includes('FARM')
      ) {
        farm.push(...el.commonChildren);
      }
    });

    // Setting the arrays to filter
    const commonChildrenArrays = [];
    if (transformer.length) {
      commonChildrenArrays.push(transformer);
    }
    if (trader.length) {
      commonChildrenArrays.push(trader);
    }
    if (stacker.length) {
      commonChildrenArrays.push(stacker);
    }
    if (farm.length) {
      commonChildrenArrays.push(farm);
    }

    // If there are no children or only one child, we return the data as it is
    if (!commonChildrenArrays.length || commonChildrenArrays.length === 1) {
      return data;
    }

    // Filtering out the children not in common
    const filteredChildren: TreeFilterData[] = findCommonObjects(commonChildrenArrays);

    console.log('filteredChildren', filteredChildren);

    data.forEach((el, i) => {
      if (el.commonChildren && el.commonChildren.length) {
        delete data[i].commonChildren;
      }
    });

    const result = [...data, ...filteredChildren];
    console.log('result', result);
    return result;
  }

  //Returns the passed data mapping all children ids and company ids, we could use the full object but can be
  // too heavy there can be thosand of values, this solution keeps the memory usage lower
  mapChildrenIds(data, selectedFarmsOnly: boolean = false) {
    const suppliers = this.commonContractDataService.getSuppliers();

    // Setting the filtered data array
    const mappedData: any[] = [];

    // Looping through the first layer of data to push only data that contains the role
    data.forEach((el) => {
      const obj = {};
      obj['companyId'] = el.companyId;

      // Mapping the user id too
      const foundElement = suppliers.find((supplier) => supplier.companyId === el.companyId);
      obj['id'] = foundElement ? foundElement.id : null;

      if (foundElement) {
        if (!selectedFarmsOnly) {
          mappedData.push(obj);

          // If a farm in the select is selected we pass in the chilren array only the farms selected
        } else {
          if (foundElement.role !== 'FARM') {
            mappedData.push(obj);
          } else {
            if (
              this.farmFilter$
                .getValue()
                .map((el) => el.companyId)
                .includes(foundElement.companyId)
            ) {
              mappedData.push(obj);
            }
          }
        }
      }

      // If the element has children, we loop trough them too to find the role
      if (el.commonChildren && el.commonChildren.length) {
        el.commonChildren.forEach((child) => {
          const obj = {};
          obj['companyId'] = child.companyId;

          // Mapping the user id too
          const foundChild = suppliers.find((supplier) => supplier.companyId === child.companyId);
          obj['id'] = foundChild ? foundChild.id : null;

          if (foundChild) {
            if (!selectedFarmsOnly) {
              mappedData.push(obj);

              // If a farm in the select is selected we pass in the chilren array only the farms selected
            } else {
              if (foundChild.role !== 'FARM') {
                mappedData.push(obj);
              } else {
                if (
                  this.farmFilter$
                    .getValue()
                    .map((el) => el.companyId)
                    .includes(foundChild.companyId)
                ) {
                  mappedData.push(obj);
                }
              }
            }
          }
        });
      }
    });

    return mappedData;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
